import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import sha1 from 'js-sha1';
import PropTypes from 'prop-types';
import Masonry from '@mui/lab/Masonry';
import { useMediaQuery } from '@mui/material';
import debounce from 'lodash/debounce';
import { useSelector } from 'react-redux';
import CreativeCard from '../creativeCard/CreativeCard';
import { getCreativeSectionsSize, OPTIONAL_CREATIVES_SIZES } from '../../creativeSizingUtils';
import { CreativeType } from '../../types';
import css from './CreativesGrid.css';
import { AutoCompleteOptionType } from '../../../components/molecules/autocompleteTypes';
import { LayoutConfig } from '../../consts';
import { getAdminModeEnabled } from '../../../selectors/user';

const NUMBER_OF_ROWS_TO_LOAD = 15;

export const SKELETONS = [...Array(12)].map(index => ({
    ...OPTIONAL_CREATIVES_SIZES[index % OPTIONAL_CREATIVES_SIZES.length],
}));

export const CREATIVES_GRID_TEST_ID = 'creatives-grid';
export const LOADING_GRID_TEST_ID = 'creatives-grid-loading';

const CreativesGrid = forwardRef(
    (
        {
            creatives = [],
            onCreativeClick = () => {},
            isLoading,
            loadingSections,
            selectedMetrics,
            selectedCohorts,
            layout,
        },
        ref
    ) => {
        const lastCreativeRef = useRef(null);

        const isMobile = useMediaQuery(theme => theme.breakpoints.down('mobile'));
        const isTablet = useMediaQuery(theme => theme.breakpoints.between('mobile', 'tablet'));
        const isXs = useMediaQuery(theme => theme.breakpoints.between('tablet', 'sm'));
        const isSm = useMediaQuery(theme => theme.breakpoints.between('sm', 'md'));
        const isMd = useMediaQuery(theme => theme.breakpoints.between('md', 'lg'));
        const isLg = useMediaQuery(theme => theme.breakpoints.between('lg', 'xl'));
        const isXl = useMediaQuery(theme => theme.breakpoints.up('xl'));
        const isAdminMode = useSelector(getAdminModeEnabled);

        const columns = isMobile ? 1 : isTablet ? 2 : isXs ? 3 : isSm ? 4 : isMd ? 5 : isLg ? 6 : isXl ? 7 : 4;

        const [numberOfDisplayedItems, setNumberOfDisplayedItems] = useState(columns * NUMBER_OF_ROWS_TO_LOAD);

        const items = isLoading ? SKELETONS : creatives;
        const itemsToDisplay = items.slice(0, numberOfDisplayedItems);

        // Reset pagination when switching between loading states
        useEffect(() => {
            if (isLoading) {
                lastCreativeRef.current = null;
            } else {
                // When transitioning from loading to loaded, reset to initial page size
                setNumberOfDisplayedItems(columns * NUMBER_OF_ROWS_TO_LOAD);
            }
        }, [isLoading, columns]);

        // Adjust displayed items when columns change or when we have fewer items than expected
        useEffect(() => {
            if (isLoading) return; // Don't adjust during loading

            const currentRows = Math.ceil(numberOfDisplayedItems / columns);
            const expectedRows = Math.max(NUMBER_OF_ROWS_TO_LOAD, currentRows);
            const newNumberOfItems = Math.min(creatives.length, columns * expectedRows);

            if (newNumberOfItems !== numberOfDisplayedItems) {
                setNumberOfDisplayedItems(newNumberOfItems);
            }
        }, [columns, creatives.length, isLoading]);

        const loadMoreItems = debounce(
            () => {
                if (isLoading || items.length === numberOfDisplayedItems) return;

                const numberOfItemsToLoad = columns * NUMBER_OF_ROWS_TO_LOAD;

                setNumberOfDisplayedItems(numberOfItems => Math.min(items.length, numberOfItems + numberOfItemsToLoad));
            },
            100,
            { leading: true, trailing: true }
        );

        const onScroll = e => {
            // Don't handle scroll events during loading state
            if (isLoading || !lastCreativeRef.current) return;

            const { target } = e;
            if (!target.getBoundingClientRect || !lastCreativeRef.current.getBoundingClientRect) return;

            // Additional safety check to ensure the element is actually rendered and has dimensions
            const lastCreativeRect = lastCreativeRef.current.getBoundingClientRect();
            if (!lastCreativeRect.height || !lastCreativeRect.width) return;

            const container = target.getBoundingClientRect();

            // Check if the last creative's top is within the container's view
            if (target.clientHeight <= lastCreativeRect.top && lastCreativeRect.top <= container.bottom) {
                loadMoreItems();
            }
        };

        const onClick = imageHash => {
            // If there's a text selection, do nothing
            const selection = window.getSelection();
            if (selection && selection.toString().length > 0) {
                return;
            }

            onCreativeClick(imageHash);
        };

        // Allow the scrollable container to load more creatives to the grid
        useImperativeHandle(ref, () => ({
            onScroll(e) {
                onScroll(e);
            },
        }));

        const getCreativeCardKey = (element, index) => {
            const { imageHash, embeddingUrl, height, width, videoDurationRange } = element;

            if (!imageHash) return index;

            // Building the key from multiple properties of the creative to avoid duplicates keys (rare)
            return sha1(`${imageHash}${embeddingUrl}${height}${width}${videoDurationRange}`);
        };

        const cardHeightConfig = useMemo(() => {
            if (layout !== LayoutConfig.GRID.type) return null;
            return getCreativeSectionsSize(itemsToDisplay, selectedMetrics, selectedCohorts, isAdminMode);
        }, [itemsToDisplay, selectedMetrics, selectedCohorts, layout, isAdminMode]);

        return (
            <div className={css.creativesGrid} onScroll={onScroll}>
                <Masonry
                    columns={columns}
                    spacing={2}
                    className={css.masonry}
                    data-testid={isLoading ? LOADING_GRID_TEST_ID : CREATIVES_GRID_TEST_ID}
                >
                    {itemsToDisplay.map((element, index) => {
                        const isLastItem = index === itemsToDisplay.length - 1;
                        return (
                            <CreativeCard
                                key={getCreativeCardKey(element, index)}
                                onClick={onClick}
                                ref={isLastItem && !isLoading ? lastCreativeRef : null}
                                loadingSections={loadingSections}
                                selectedMetrics={selectedMetrics}
                                selectedCohorts={selectedCohorts}
                                layout={layout}
                                cardHeightConfig={cardHeightConfig}
                                isAdminMode={isAdminMode}
                                {...element}
                            />
                        );
                    })}
                </Masonry>
            </div>
        );
    }
);

CreativesGrid.propTypes = {
    creatives: PropTypes.arrayOf(PropTypes.shape(CreativeType)),
    onCreativeClick: PropTypes.func,
    isLoading: PropTypes.bool.isRequired,
    loadingSections: PropTypes.arrayOf(PropTypes.string),
    selectedMetrics: PropTypes.arrayOf(AutoCompleteOptionType),
    selectedCohorts: PropTypes.arrayOf(AutoCompleteOptionType),
    layout: PropTypes.string,
};

export default CreativesGrid;
